
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Trunk, TrunkItem } from '@shared/index';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { CardModule } from 'primeng/card';
import { DrawerModule } from 'primeng/drawer';
import { IconFieldModule } from 'primeng/iconfield';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputIconModule } from 'primeng/inputicon';
import { InputText } from "primeng/inputtext";
import { Menu, MenuModule } from 'primeng/menu';
import { MessageModule } from 'primeng/message';
import { TableModule } from 'primeng/table';
import { BulkAction, BulkActionBarComponent } from 'src/app/shared/components/bulk-action-bar/bulk-action-bar.component';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule as PrimeTooltipModule } from 'primeng/tooltip';
import { BotService } from 'src/app/shared/services/bot.service';
import { CounterService } from '@shared-angular/services/counter.service';
import { RouteShareService } from 'src/app/shared/services/route-share.service';
import { ToastrService } from '@shared-angular/services/toastr.service';
import { TrunkService } from 'src/app/shared/services/trunk.service';
import { SkillIdInputComponent } from "../../../../../../shared/components/ai-bot/utils/skill-id-input/skill-id-input.component";
import { SkillUsageListComponent } from "../../../../../../shared/components/ai-bot/utils/skill-usage-list/skill-usage-list.component";
import { ManualLinkComponent } from "../../../../../../shared/components/rag-admin/manual-link/manual-link.component";
import { DatabasesTabDetailComponent } from './databases-tab-detail/databases-tab-detail.component';

@Component({
  imports: [
    FormsModule,
    TooltipModule,
    SkillIdInputComponent,
    SkillUsageListComponent,
    ManualLinkComponent,
    MenuModule,
    ButtonModule,
    ConfirmDialogModule,
    TableModule,
    BulkActionBarComponent,
    ToggleSwitchModule,
    PrimeTooltipModule,
    CardModule,
    InputText,
    InputGroupModule,
    InputGroupAddonModule,
    IconFieldModule,
    InputIconModule,
    MessageModule,
    DrawerModule,
    DatabasesTabDetailComponent
  ],
  styleUrl: './databases-tab.component.scss',
  templateUrl: './databases-tab.component.html',
  providers: [ConfirmationService]
})
export class DatabasesTabComponent implements OnInit {
  public get bulkActions(): BulkAction[] {
    return [
      {
        id: 'bulk-remove',
        label: $localize`:@@bulk-remove:Entfernen`,
        icon: 'ri ri-delete-bin-line',
        command: () => this.removeSelectedDatabases()
      }
    ];
  }

  public newDatabaseDescription: string = '';
  public selectedItemIndex: number = -1;
  protected isDbAddingInProgress: boolean = false;
  public selectedTrunks: Trunk[] = [];

  public get allSelected(): boolean {
    return this.rss.datatrunks.length > 0 && this.selectedTrunks.length === this.rss.datatrunks.length;
  }

  public get selectedItem(): TrunkItem | undefined {
    return this.rss.datatrunk?.items[this.selectedItemIndex];
  }

  constructor(
    private router: Router,
    private toastr: ToastrService,
    private trunkService: TrunkService,
    public counter: CounterService,
    public rss: RouteShareService,
    private botService: BotService,
    private route: ActivatedRoute,
    private confirmationService: ConfirmationService
  ) { }

  public ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      if (params['open']) {
        this.showDetailDrawer(params['open'] as string);
      }
    });
  }

  public async addDatabase() {
    this.isDbAddingInProgress = true;
    const trunk: Omit<Trunk, "_id"> = {
      botId: this.rss.botConfig._id,
      skillId: '',
      placeholder: '',
      trunkDescription: this.newDatabaseDescription.trim(),
      keyDescription: "",
      active: true
    };
    trunk.placeholder = await this.botService.suggestName(this.rss.botConfig._id, trunk.trunkDescription);
    trunk.skillId = await this.botService.suggestSkill(this.rss.botConfig._id, trunk.trunkDescription, trunk.placeholder);
    const res = await this.trunkService.createTrunk(trunk);
    this.rss.datatrunks.unshift({ ...res.trunk, items: [] });
    this.newDatabaseDescription = '';
    this.toastr.success("CREATED");
    this.isDbAddingInProgress = false;
    this.showDetailDrawer(res.trunk._id);
  }

  public async removeDatabase(trunkId: string, showToast = true) {
    await this.trunkService.deleteTrunk(trunkId);
    this.counter.removeTrunk(this.rss.botConfig._id);
    this.rss.datatrunks.splice(this.rss.datatrunks.findIndex(trunk => trunk._id == trunkId), 1);
    this.selectedTrunks = this.selectedTrunks.filter(trunk => trunk._id !== trunkId);
    if (showToast) this.toastr.success("DELETED");
  }

  public toggleAll(checked: boolean): void {
    this.selectedTrunks = checked ? [...this.rss.datatrunks] : [];
  }

  public removeSelectedDatabases(): void {
    this.confirmationService.confirm({
      header: $localize`:@@bulk-delete-skills-header:Ausgewählte Einträge löschen?`,
      icon: 'ri-error-warning-line',
      message: $localize`:@@bulk-delete-skills-message:Möchtest du die ausgewählten Einträge wirklich löschen?`,
      rejectButtonProps: {
        label: $localize`:@@delete-reject:Abbrechen`,
        severity: 'secondary',
        outlined: true
      },
      acceptButtonProps: {
        label: $localize`:@@delete:Löschen`,
        severity: 'danger'
      },
      accept: async () => {
        const trunks = [...this.selectedTrunks];
        for (const trunk of trunks) {
          await this.removeDatabase(trunk._id, false);
        }
        this.selectedTrunks = [];
        this.toastr.success("DELETED");
      }
    });
  }

  public async updateDatabase(trunk: Trunk) {
    try {
      await this.trunkService.updateTrunk(trunk);
      this.toastr.success("SAVED");
    } catch (error: unknown) {
      const errorType = (error as { error?: { errorType?: string } }).error?.errorType;
      if (errorType === 'SKILL_ID_DUPLICATE') {
        this.toastr.error("SAVED", $localize`:@@error-skill-id-duplicate:Diese Skill-ID wird bereits von einem anderen Skill in diesem Bot verwendet. Wähle eine andere Skill-ID.`);
      } else if (errorType === 'FUNCTION_NAME_DUPLICATE') {
        this.toastr.error("SAVED", $localize`:@@error-function-name-duplicate:Dieser Funktionsname wird bereits von einem anderen Skill in diesem Bot verwendet. Wähle einen anderen Namen.`);
      }
    }
  }

  public checkForAdding(): boolean {
    if (this.newDatabaseDescription.trim() == "") return true;
    return this.isDbAddingInProgress;
  }

  public dataBaseContextMenu: MenuItem[] = [];
  public selectedTrunk: Trunk | null = null;
  public selectedTrunkId: string = '';
  public detailDrawer: boolean = false;

  public showDetailDrawer(trunkId: string): void {
    this.selectedTrunkId = trunkId;
    this.rss.datatrunk = this.rss.datatrunks.find(trunk => trunk._id === trunkId);
    this.detailDrawer = true;
  }

  public openDatabaseMenu(event: Event, menu: Menu, trunk: Trunk) {
    this.selectedTrunk = trunk;
    this.dataBaseContextMenu = this.buildDataBaseMenuItems(trunk);
    menu.toggle(event);
  }

  private buildDataBaseMenuItems(trunk: Trunk): MenuItem[] {
    const items: MenuItem[] = [];
    items.push({
      label: $localize`:@@edit:Bearbeiten`,
      icon: 'ri ri-pencil-fill',
      command: () => this.showDetailDrawer(trunk._id)
    });

    items.push({
      label: $localize`:@@remove-doc-acces:Entfernen`,
      icon: 'ri ri-close-large-fill',
      command: () => this.removeDatabase(trunk._id)
    });

    return items;
  }
}
